import { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import { api } from '../api';
import { useAuth } from '../context/AuthContext'; // usuario activo del panel

const MESES_ES = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio',
                  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function mesLabel(yyyymm) {
  if (!yyyymm) return '';
  const [a, m] = yyyymm.split('-');
  return `${MESES_ES[parseInt(m)]} ${a}`;
}

function fmt(n, dec = 2) {
  return Number(n || 0).toLocaleString('es-EC', {
    minimumFractionDigits: dec, maximumFractionDigits: dec
  });
}

function hoyMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const TooltipDia = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{ background: '#1e1e2e', border: '1px solid #2a2a3e',
      borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ color: '#fff', fontWeight: 700, marginBottom: 6 }}>Día {d?.dia?.slice(-2)}</div>
      <div style={{ color: '#4cc9f0' }}>Total: <strong>${fmt(d?.total)}</strong></div>
      <div style={{ color: '#4caf50' }}>Contado: ${fmt(d?.contado)}</div>
      <div style={{ color: '#ffc107' }}>Crédito: ${fmt(d?.credito)}</div>
      <div style={{ color: '#aaa', marginTop: 4 }}>Facturas: {d?.facturas}</div>
    </div>
  );
};

const TooltipHistorico = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{ background: '#1e1e2e', border: '1px solid #2a2a3e',
      borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ color: '#fff', fontWeight: 700, marginBottom: 6 }}>{d?.label}</div>
      <div style={{ color: '#4cc9f0' }}>Neto: <strong>${fmt(d?.total)}</strong></div>
      {d?.devoluciones > 0 && (
        <div style={{ color: '#e63946' }}>NC: -${fmt(d?.devoluciones)}</div>
      )}
      <div style={{ color: '#aaa' }}>Facturas: {d?.facturas}</div>
      <div style={{ color: '#aaa' }}>Clientes: {d?.clientes_unicos}</div>
      {d?.delta_pct !== null && d?.delta_pct !== undefined && (
        <div style={{ color: d.delta_pct >= 0 ? '#4caf50' : '#e63946', marginTop: 4 }}>
          {d.delta_pct >= 0 ? '▲' : '▼'} {Math.abs(d.delta_pct)}% vs mes ant.
        </div>
      )}
    </div>
  );
};

function KPICard({ label, value, color, prefix = '$', delta, sub }) {
  return (
    <div style={{ background: '#1e1e2e', borderRadius: 12,
      padding: '16px 20px', borderTop: `3px solid ${color}`, flex: 1 }}>
      <div style={{ color: '#aaa', fontSize: 12, marginBottom: 6 }}>{label}</div>
      <div style={{ color, fontSize: 24, fontWeight: 700, fontFamily: 'monospace' }}>
        {prefix}{typeof value === 'number' ? fmt(value) : value}
      </div>
      {sub && <div style={{ color: '#555', fontSize: 11, marginTop: 3 }}>{sub}</div>}
      {delta !== null && delta !== undefined && (
        <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700,
          color: delta >= 0 ? '#4caf50' : '#e63946' }}>
          {delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}% vs mes anterior
        </div>
      )}
    </div>
  );
}

function BadgePago({ forma }) {
  const f = (forma || '').toUpperCase();
  const cfg = f === 'CONTADO'
    ? { color: '#4caf50', bg: '#0d2218', label: 'Contado' }
    : { color: '#ffc107', bg: '#2a1e00', label: 'Crédito' };
  return (
    <span style={{ background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.color}44`, borderRadius: 5,
      padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>
      {cfg.label}
    </span>
  );
}

export default function VentasDashboard() {
  const { usuario } = useAuth();
  const usuarioKey = (usuario?.usuario || '').toLowerCase();
  const PUEDE_VALIDAR = ['admin', 'gaby'].includes(usuarioKey);

  const [mes,          setMes]          = useState(hoyMes());
  const [data,         setData]         = useState(null);
  const [historico,    setHistorico]    = useState(null);
  const [periodoHist,  setPeriodoHist]  = useState(6);
  const [cargando,     setCargando]     = useState(true);
  const [tab,          setTab]          = useState('resumen');
  const [devoluciones, setDevoluciones] = useState(null);
  const [cargandoDev,  setCargandoDev]  = useState(false);
  const [modalDev,     setModalDev]     = useState(null);
  const [validaciones, setValidaciones] = useState({});  // devid → datos validación
  const [validando,    setValidando]    = useState(false);
  const [comentario,   setComentario]   = useState('');

  const cargar = useCallback(() => {
    setCargando(true);
    api.get(`/ventas/dashboard?mes=${mes}`)
      .then(d => { setData(d.error ? null : d); setCargando(false); })
      .catch(() => setCargando(false));
  }, [mes]);

  const cargarHistorico = useCallback(() => {
    api.get(`/ventas/historico?meses=${periodoHist}`)
      .then(d => setHistorico(d.error ? null : d));
  }, [periodoHist]);

  const cargarDevoluciones = useCallback(() => {
    setCargandoDev(true);
    Promise.all([
      api.get(`/ventas/devoluciones?mes=${mes}`),
      api.get(`/ventas/devoluciones/validaciones?mes=${mes}`),
    ]).then(([devs, vals]) => {
      setDevoluciones(devs.error ? null : devs);
      setValidaciones(vals.validaciones || {});
      setCargandoDev(false);
    }).catch(() => setCargandoDev(false));
  }, [mes]);

  const abrirDetalleDev = async (devid) => {
    setModalDev({ cargando: true, devid });
    setComentario('');
    const d = await api.get(`/ventas/devoluciones/${devid}`);
    setModalDev(d.error ? null : d);
  };

  const validarNC = async () => {
    if (!modalDev || validando) return;
    setValidando(true);
    const resp = await api.post(
      `/ventas/devoluciones/${modalDev.devid}/validar`, {
        validado_por:   usuarioKey,
        comentario,
        devolucion:     modalDev.devolucion,
        mes,
        riesgo:         modalDev.riesgo,
        mismo_vendedor: modalDev.mismo_vendedor,
      }
    );
    if (resp.ok) {
      // Actualizar validaciones localmente sin recargar todo
      setValidaciones(prev => ({
        ...prev,
        [modalDev.devid]: {
          validado_por: resp.validado_por,
          fecha:        resp.fecha,
          comentario,
          riesgo:       modalDev.riesgo,
        }
      }));
      setComentario('');
    }
    setValidando(false);
  };

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (tab === 'historial')    cargarHistorico(); }, [tab, cargarHistorico]);
  useEffect(() => { if (tab === 'devoluciones') cargarDevoluciones(); }, [tab, cargarDevoluciones]);

  const mesesOpts = [];
  const hoy = new Date();
  for (let i = 0; i < 12; i++) {
    const d   = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    mesesOpts.push({ val, label: mesLabel(val) });
  }

  const kpis         = data?.kpis || {};
  const por_dia      = data?.por_dia || [];
  const por_vendedor = data?.por_vendedor || [];
  const top_clientes = data?.top_clientes || [];
  const ultimas      = data?.ultimas || [];

  const promDia = por_dia.length > 0
    ? por_dia.reduce((s, d) => s + d.total, 0) / por_dia.length : 0;

  const COLORES_VEN = ['#4cc9f0', '#f72585', '#4caf50', '#ffc107', '#7209b7'];

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", color: '#e0e0e0' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ color: '#fff', margin: 0, fontSize: 22, fontWeight: 700 }}>
            📊 Ventas
          </h2>
          <div style={{ color: '#555', fontSize: 13, marginTop: 3 }}>
            Datos en tiempo real desde Morpheus
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={mes} onChange={e => setMes(e.target.value)} style={selStyle}>
            {mesesOpts.map(o => (
              <option key={o.val} value={o.val}>{o.label}</option>
            ))}
          </select>
          <button onClick={cargar} style={btnStyle}>🔄</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24,
        borderBottom: '1px solid #2a2a3e' }}>
        {[
          { key: 'resumen',       label: '📈 Resumen' },
          { key: 'vendedores',    label: '👤 Por Vendedor' },
          { key: 'clientes',      label: '🏆 Top Clientes' },
          { key: 'historial',     label: '📅 Histórico' },
          { key: 'devoluciones',  label: '⚠️ Devoluciones',
            color: devoluciones?.resumen?.con_riesgo > 0 ? '#e63946' : undefined },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            background: 'none', border: 'none',
            borderBottom: `2px solid ${tab === t.key ? (t.color || '#4361ee') : 'transparent'}`,
            color: tab === t.key ? (t.color || '#4361ee') : (t.color ? t.color + '99' : '#555'),
            fontWeight: tab === t.key ? 700 : 400,
            fontSize: 13, padding: '8px 16px', cursor: 'pointer',
          }}>{t.label}</button>
        ))}
      </div>

      {cargando ? (
        <div style={{ color: '#555', textAlign: 'center', padding: 60 }}>
          Cargando datos de Morpheus...
        </div>
      ) : !data ? (
        <div style={{ color: '#e63946', textAlign: 'center', padding: 40 }}>
          No se pudieron cargar los datos
        </div>
      ) : (
        <>
          {/* ══ RESUMEN ══ */}
          {tab === 'resumen' && (
            <>
              {/* KPIs */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                <KPICard
                  label="Ventas del mes"
                  value={kpis.total}
                  color="#4cc9f0"
                  delta={kpis.delta_total}
                  sub={kpis.devoluciones > 0
                    ? `Bruto $${fmt(kpis.total_bruto)} · NC -$${fmt(kpis.devoluciones)} (${kpis.nc_count})`
                    : `Ant: $${fmt(kpis.mes_anterior?.total)}`}
                />
                <KPICard
                  label="Facturas"
                  value={kpis.facturas}
                  color="#f72585"
                  prefix=""
                  delta={kpis.delta_facturas}
                  sub={`Ant: ${kpis.mes_anterior?.facturas}`}
                />
                <KPICard
                  label="Ticket promedio"
                  value={kpis.ticket_prom}
                  color="#4caf50"
                  sub={`Ant: $${fmt(kpis.mes_anterior?.ticket_prom)}`}
                />
                <KPICard
                  label="Clientes únicos"
                  value={kpis.clientes_unicos}
                  color="#ffc107"
                  prefix=""
                />
              </div>

              {/* Contado / Crédito */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                {[
                  { label: 'Contado', value: kpis.contado,
                    pct: kpis.total > 0 ? Math.round(kpis.contado / kpis.total * 100) : 0,
                    color: '#4caf50' },
                  { label: 'Crédito', value: kpis.credito,
                    pct: kpis.total > 0 ? Math.round(kpis.credito / kpis.total * 100) : 0,
                    color: '#ffc107' },
                ].map(item => (
                  <div key={item.label} style={{ flex: 1, background: '#1e1e2e',
                    borderRadius: 10, padding: '14px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ color: '#aaa', fontSize: 13 }}>{item.label}</span>
                      <span style={{ color: item.color, fontWeight: 700, fontSize: 13 }}>
                        {item.pct}%
                      </span>
                    </div>
                    <div style={{ color: item.color, fontFamily: 'monospace',
                      fontWeight: 700, fontSize: 20, marginBottom: 8 }}>
                      ${fmt(item.value)}
                    </div>
                    <div style={{ height: 6, background: '#2a2a3e',
                      borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${item.pct}%`, height: '100%',
                        background: item.color, borderRadius: 3 }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Gráfico por día */}
              <div style={{ background: '#1e1e2e', borderRadius: 12,
                padding: '20px 24px', marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
                    Ventas por día — {data.mes_label}
                  </span>
                  <span style={{ color: '#555', fontSize: 12 }}>
                    Prom. diario: ${fmt(promDia)}
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={por_dia} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" vertical={false} />
                    <XAxis dataKey="label" stroke="#555" fontSize={11} tick={{ fill: '#888' }} />
                    <YAxis stroke="#555" fontSize={11} tick={{ fill: '#888' }}
                      tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<TooltipDia />} />
                    <ReferenceLine y={promDia} stroke="#444" strokeDasharray="4 4"
                      label={{ value: 'Prom', fill: '#555', fontSize: 10, position: 'insideRight' }} />
                    <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                      {por_dia.map((d, i) => (
                        <Cell key={i}
                          fill={d.total >= promDia ? '#4cc9f0' : '#4361ee'}
                          fillOpacity={0.85} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* ── Últimas 10 facturas ── */}
              <div style={{ background: '#1e1e2e', borderRadius: 12, padding: '20px 24px' }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
                  🧾 Últimas facturas — {data.mes_label}
                </div>

                <div style={{ display: 'grid',
                  gridTemplateColumns: '160px 1fr 90px 100px 90px',
                  padding: '8px 12px', gap: 8,
                  background: '#13131f', borderRadius: '8px 8px 0 0',
                  color: '#555', fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  <span>Factura / Fecha</span>
                  <span>Cliente</span>
                  <span style={{ textAlign: 'right' }}>Total</span>
                  <span style={{ textAlign: 'center' }}>Pago</span>
                  <span>Vendedor</span>
                </div>

                {ultimas.length === 0 ? (
                  <div style={{ color: '#555', textAlign: 'center', padding: 24,
                    background: '#1a1a2e', borderRadius: '0 0 8px 8px' }}>
                    Sin facturas en este período
                  </div>
                ) : (
                  <div style={{ borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                    {ultimas.map((f, i) => (
                      <div key={`${f.factura}-${i}`} style={{
                        display: 'grid',
                        gridTemplateColumns: '160px 1fr 90px 100px 90px',
                        padding: '10px 12px', gap: 8, alignItems: 'center',
                        background: i % 2 === 0 ? '#1a1a2e' : '#161626',
                        borderBottom: '1px solid #13131f',
                      }}>
                        <div>
                          <div style={{ color: '#4cc9f0', fontFamily: 'monospace',
                            fontSize: 11 }}>{f.factura}</div>
                          <div style={{ color: '#444', fontSize: 10, marginTop: 2 }}>
                            {(f.fecha || '').slice(0, 16).replace('T', ' ')}
                          </div>
                        </div>
                        <div style={{ color: '#ccc', fontSize: 12,
                          whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis' }}>
                          {f.cliente || 'Consumidor Final'}
                        </div>
                        <div style={{ textAlign: 'right', color: '#fff',
                          fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>
                          ${fmt(f.total)}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <BadgePago forma={f.forma_pago} />
                        </div>
                        <div style={{ color: '#555', fontSize: 11,
                          whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis' }}>
                          {(f.vendedor || '').split(' ')[0]}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ══ VENDEDORES ══ */}
          {tab === 'vendedores' && (
            <div style={{ background: '#1e1e2e', borderRadius: 12, padding: '20px 24px' }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                Ventas por vendedor — {data.mes_label}
              </div>
              <div style={{ color: '#555', fontSize: 12, marginBottom: 20 }}>
                Total neto: ${fmt(kpis.total)}
              </div>
              {por_vendedor.map((v, i) => {
                const pct   = kpis.total > 0 ? Math.round(v.total / kpis.total * 100) : 0;
                const color = COLORES_VEN[i % COLORES_VEN.length];
                return (
                  <div key={v.vendedor} style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: `${color}22`, border: `1.5px solid ${color}55`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color, fontWeight: 700, fontSize: 11,
                        }}>
                          {v.vendedor.split(' ').map(w => w[0]).join('').slice(0, 2)}
                        </div>
                        <div>
                          <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                            {v.vendedor}
                          </div>
                          <div style={{ color: '#555', fontSize: 11 }}>
                            {v.facturas} facturas · ticket ${fmt(v.ticket_prom)}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color, fontFamily: 'monospace',
                          fontWeight: 700, fontSize: 18 }}>${fmt(v.total)}</div>
                        <div style={{ color: '#555', fontSize: 11 }}>{pct}% del total</div>
                      </div>
                    </div>
                    <div style={{ height: 8, background: '#2a2a3e',
                      borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%',
                        background: color, borderRadius: 4, transition: 'width .5s' }} />
                    </div>
                  </div>
                );
              })}
              {por_vendedor.length > 1 && (
                <div style={{ marginTop: 24 }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={por_vendedor}
                      margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" vertical={false} />
                      <XAxis dataKey="vendedor" stroke="#555" fontSize={11}
                        tick={{ fill: '#888' }}
                        tickFormatter={v => v.split(' ')[0]} />
                      <YAxis stroke="#555" fontSize={11} tick={{ fill: '#888' }}
                        tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip
                        contentStyle={{ background: '#1e1e2e', border: '1px solid #2a2a3e',
                          borderRadius: 8, fontSize: 12 }}
                        formatter={(v) => [`$${fmt(v)}`, 'Ventas']}
                      />
                      <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                        {por_vendedor.map((_, i) => (
                          <Cell key={i}
                            fill={COLORES_VEN[i % COLORES_VEN.length]}
                            fillOpacity={0.85} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* ══ TOP CLIENTES ══ */}
          {tab === 'clientes' && (
            <div style={{ background: '#1e1e2e', borderRadius: 12, padding: '20px 24px' }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                Top clientes — {data.mes_label}
              </div>
              <div style={{ color: '#555', fontSize: 12, marginBottom: 16 }}>
                Excluyendo Consumidor Final
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {top_clientes.map((c, i) => {
                  const pct = kpis.total > 0
                    ? Math.round(c.total / kpis.total * 100) : 0;
                  return (
                    <div key={`${c.cedula}-${i}`} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 14px', borderRadius: 8,
                      background: i % 2 === 0 ? '#13131f' : '#1a1a2e',
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: i < 3 ? '#ffc10722' : '#2a2a3e',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: i < 3 ? '#ffc107' : '#555',
                        fontWeight: 700, fontSize: 12, flexShrink: 0,
                      }}>{i + 1}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#ccc', fontSize: 13,
                          whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis' }}>{c.nombre}</div>
                        <div style={{ color: '#555', fontSize: 10, marginTop: 2 }}>
                          {c.facturas} factura{c.facturas > 1 ? 's' : ''}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ color: '#4cc9f0', fontFamily: 'monospace',
                          fontWeight: 700, fontSize: 14 }}>${fmt(c.total)}</div>
                        <div style={{ color: '#555', fontSize: 10 }}>{pct}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ══ HISTÓRICO ══ */}
          {tab === 'historial' && (
            <div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 16, justifyContent: 'flex-end' }}>
                {[3, 6, 12].map(n => (
                  <button key={n} onClick={() => setPeriodoHist(n)} style={{
                    background: periodoHist === n ? '#1a2a3e' : '#1e1e2e',
                    border: `1px solid ${periodoHist === n ? '#4361ee' : '#2a2a3e'}`,
                    color: periodoHist === n ? '#4361ee' : '#555',
                    borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer',
                  }}>Últimos {n}m</button>
                ))}
              </div>

              {!historico ? (
                <div style={{ color: '#555', textAlign: 'center', padding: 40 }}>
                  Cargando histórico...
                </div>
              ) : (
                <>
                  <div style={{ background: '#1e1e2e', borderRadius: 12,
                    padding: '20px 24px', marginBottom: 20 }}>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
                      Tendencia de ventas — últimos {historico.meses} meses
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={historico.historico}
                        margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" vertical={false} />
                        <XAxis dataKey="label_corto" stroke="#555" fontSize={11}
                          tick={{ fill: '#888' }} />
                        <YAxis stroke="#555" fontSize={11} tick={{ fill: '#888' }}
                          tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip content={<TooltipHistorico />} />
                        <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                          {historico.historico.map((d, i) => (
                            <Cell key={i}
                              fill={d.delta_pct === null ? '#4cc9f0' :
                                    d.delta_pct >= 0 ? '#4caf50' : '#e63946'}
                              fillOpacity={0.85} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div style={{ background: '#1e1e2e', borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ display: 'grid',
                      gridTemplateColumns: '1fr 70px 100px 100px 110px 80px 70px',
                      padding: '10px 16px', background: '#13131f', gap: 8,
                      color: '#555', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
                      <span>Mes</span>
                      <span style={{ textAlign: 'center' }}>Facts.</span>
                      <span style={{ textAlign: 'right' }}>Contado</span>
                      <span style={{ textAlign: 'right' }}>Crédito</span>
                      <span style={{ textAlign: 'right' }}>Neto</span>
                      <span style={{ textAlign: 'right' }}>NC</span>
                      <span style={{ textAlign: 'center' }}>Δ</span>
                    </div>
                    {[...historico.historico].reverse().map((m, i) => (
                      <div key={m.mes} style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 70px 100px 100px 110px 80px 70px',
                        padding: '11px 16px', gap: 8, alignItems: 'center',
                        background: i % 2 === 0 ? '#1e1e2e' : '#1a1a2e',
                        borderBottom: '1px solid #13131f',
                      }}>
                        <span style={{ color: '#fff', fontWeight: 600 }}>{m.label}</span>
                        <span style={{ textAlign: 'center', color: '#aaa' }}>{m.facturas}</span>
                        <span style={{ textAlign: 'right', color: '#4caf50',
                          fontFamily: 'monospace', fontSize: 12 }}>${fmt(m.contado)}</span>
                        <span style={{ textAlign: 'right', color: '#ffc107',
                          fontFamily: 'monospace', fontSize: 12 }}>${fmt(m.credito)}</span>
                        <span style={{ textAlign: 'right', color: '#4cc9f0',
                          fontFamily: 'monospace', fontWeight: 700 }}>${fmt(m.total)}</span>
                        <span style={{ textAlign: 'right', color: '#e63946',
                          fontFamily: 'monospace', fontSize: 11 }}>
                          {m.devoluciones > 0 ? `-$${fmt(m.devoluciones)}` : '—'}
                        </span>
                        <span style={{ textAlign: 'center' }}>
                          {m.delta_pct !== null && m.delta_pct !== undefined ? (
                            <span style={{
                              background: m.delta_pct >= 0 ? '#1a3a2a' : '#3a1a1a',
                              color: m.delta_pct >= 0 ? '#4caf50' : '#e63946',
                              borderRadius: 5, padding: '2px 6px',
                              fontSize: 10, fontWeight: 700,
                            }}>
                              {m.delta_pct >= 0 ? '▲' : '▼'} {Math.abs(m.delta_pct)}%
                            </span>
                          ) : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {/* ══ DEVOLUCIONES ══ */}
          {tab === 'devoluciones' && (
            <div>
              {cargandoDev ? (
                <div style={{ color: '#555', textAlign: 'center', padding: 60 }}>
                  Cargando devoluciones...
                </div>
              ) : !devoluciones ? (
                <div style={{ color: '#e63946', textAlign: 'center', padding: 40 }}>
                  No se pudieron cargar las devoluciones
                </div>
              ) : (
                <>
                  {/* KPIs */}
                  <div style={{ display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
                    {[
                      { label: 'Total NC',         value: devoluciones.resumen.total_nc,
                        color: '#aaa',    prefix: '' },
                      { label: 'Monto devuelto',   value: `$${fmt(devoluciones.resumen.total_monto)}`,
                        color: '#e63946', prefix: '' },
                      { label: 'Con riesgo',       value: devoluciones.resumen.con_riesgo,
                        color: '#ffc107', prefix: '' },
                      { label: '⚠ Mismo vendedor', value: devoluciones.resumen.mismo_vendedor,
                        color: '#e63946', prefix: '' },
                      { label: 'Sin observación',  value: devoluciones.resumen.sin_obs,
                        color: '#f72585', prefix: '' },
                    ].map(k => (
                      <div key={k.label} style={{ background: '#1e1e2e',
                        borderRadius: 10, padding: '12px 16px',
                        borderTop: `3px solid ${k.color}` }}>
                        <div style={{ color: '#555', fontSize: 10,
                          textTransform: 'uppercase', letterSpacing: 0.5,
                          marginBottom: 6 }}>{k.label}</div>
                        <div style={{ color: k.color, fontFamily: 'monospace',
                          fontWeight: 700, fontSize: 22 }}>
                          {k.value}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Lista */}
                  {devoluciones.devoluciones.length === 0 ? (
                    <div style={{ color: '#4caf50', textAlign: 'center',
                      padding: 40, fontSize: 14 }}>
                      ✅ Sin devoluciones en {mesLabel(mes)}
                    </div>
                  ) : (
                    <div style={{ background: '#1e1e2e', borderRadius: 12,
                      overflow: 'hidden' }}>
                      {/* Header tabla */}
                      <div style={{ display: 'grid',
                        gridTemplateColumns: '140px 1fr 1fr 80px 90px 80px 80px 30px',
                        padding: '10px 16px', background: '#13131f', gap: 8,
                        color: '#555', fontSize: 10, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        <span>N° Devolución</span>
                        <span>Cliente</span>
                        <span>Factura Orig.</span>
                        <span style={{ textAlign: 'right' }}>Monto</span>
                        <span style={{ textAlign: 'center' }}>Procesó</span>
                        <span style={{ textAlign: 'center' }}>Riesgo</span>
                        <span style={{ textAlign: 'center' }}>Validado</span>
                        <span />
                      </div>

                      {devoluciones.devoluciones.map((d, i) => {
                        const RIESGO = {
                          alto:   { color: '#e63946', bg: '#2a0d0d', label: '🔴 ALTO' },
                          medio:  { color: '#ffc107', bg: '#2a1e00', label: '🟡 MEDIO' },
                          normal: { color: '#4caf50', bg: '#0d2218', label: '🟢 OK' },
                        };
                        const r = RIESGO[d.riesgo];
                        const val = validaciones[d.devid];
                        return (
                          <div key={d.devid}
                            onClick={() => abrirDetalleDev(d.devid)}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '140px 1fr 1fr 80px 90px 80px 80px 30px',
                              padding: '11px 16px', gap: 8, alignItems: 'center',
                              background: val ? '#0d1a0d' : d.riesgo === 'alto'
                                ? '#1a0808'
                                : i % 2 === 0 ? '#1e1e2e' : '#1a1a2e',
                              borderBottom: '1px solid #13131f',
                              cursor: 'pointer', transition: 'background .15s',
                              borderLeft: `3px solid ${val ? '#4caf50' :
                                d.riesgo === 'alto' ? '#e63946' :
                                d.riesgo === 'medio' ? '#ffc107' : 'transparent'}`,
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#2d2d44'}
                            onMouseLeave={e => e.currentTarget.style.background =
                              val ? '#0d1a0d' : d.riesgo === 'alto' ? '#1a0808' :
                              i % 2 === 0 ? '#1e1e2e' : '#1a1a2e'}
                          >
                            <div>
                              <div style={{ color: '#e63946', fontFamily: 'monospace',
                                fontSize: 11 }}>
                                {d.devolucion.replace('001-010-000', '')}
                              </div>
                              <div style={{ color: '#444', fontSize: 10, marginTop: 1 }}>
                                {d.fecha?.slice(0, 10)}
                              </div>
                            </div>
                            <div style={{ color: '#ccc', fontSize: 12,
                              whiteSpace: 'nowrap', overflow: 'hidden',
                              textOverflow: 'ellipsis' }}>
                              {d.cliente}
                            </div>
                            <div>
                              <div style={{ color: '#4cc9f0', fontFamily: 'monospace',
                                fontSize: 11 }}>
                                {d.factura_orig.replace('001-010-000', '')}
                              </div>
                              <div style={{ color: '#444', fontSize: 10 }}>
                                {d.forma_pago_orig}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', color: '#e63946',
                              fontFamily: 'monospace', fontWeight: 700 }}>
                              ${fmt(d.total)}
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ color: d.mismo_vendedor ? '#e63946' : '#aaa',
                                fontSize: 11, fontWeight: d.mismo_vendedor ? 700 : 400 }}>
                                {d.usuario_nc.split(' ')[0]}
                              </div>
                              {d.mismo_vendedor && (
                                <div style={{ color: '#e63946', fontSize: 9 }}>
                                  = vendedor
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <span style={{ background: r.bg, color: r.color,
                                borderRadius: 5, padding: '2px 8px',
                                fontSize: 10, fontWeight: 700 }}>
                                {r.label}
                              </span>
                            </div>
                            {/* Badge validado */}
                            <div style={{ textAlign: 'center' }}>
                              {val ? (
                                <div>
                                  <div style={{ color: '#4caf50', fontSize: 10,
                                    fontWeight: 700 }}>✅ {val.validado_por}</div>
                                  <div style={{ color: '#444', fontSize: 9 }}>
                                    {val.fecha?.slice(0, 10)}
                                  </div>
                                </div>
                              ) : (
                                <span style={{ color: '#333', fontSize: 10 }}>—</span>
                              )}
                            </div>
                            <div style={{ color: '#555', fontSize: 14,
                              textAlign: 'center' }}>›</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* ══ MODAL DETALLE NC ══ */}
      {modalDev && (
        <>
          <div onClick={() => setModalDev(null)} style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.75)', zIndex: 300,
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%,-50%)',
            background: '#1a1a2e', borderRadius: 14,
            width: 620, maxHeight: '85vh',
            zIndex: 301, border: '1px solid #2d2d44',
            boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {modalDev.cargando ? (
              <div style={{ padding: 40, color: '#555', textAlign: 'center' }}>
                Cargando detalle...
              </div>
            ) : (
              <>
                {/* Header modal */}
                <div style={{ padding: '16px 22px',
                  borderBottom: '1px solid #2d2d44',
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ color: '#e63946', fontFamily: 'monospace',
                        fontWeight: 700, fontSize: 15 }}>
                        {modalDev.devolucion}
                      </span>
                      <span style={{
                        background: modalDev.riesgo === 'alto' ? '#2a0d0d' :
                                    modalDev.riesgo === 'medio' ? '#2a1e00' : '#0d2218',
                        color: modalDev.riesgo === 'alto' ? '#e63946' :
                               modalDev.riesgo === 'medio' ? '#ffc107' : '#4caf50',
                        borderRadius: 5, padding: '2px 10px',
                        fontSize: 11, fontWeight: 700,
                      }}>
                        {modalDev.riesgo === 'alto' ? '🔴 RIESGO ALTO' :
                         modalDev.riesgo === 'medio' ? '🟡 RIESGO MEDIO' : '🟢 OK'}
                      </span>
                    </div>
                    <div style={{ color: '#555', fontSize: 12, marginTop: 4 }}>
                      {modalDev.fecha} · ${fmt(modalDev.total)}
                    </div>
                  </div>
                  <button onClick={() => setModalDev(null)} style={{
                    background: 'none', border: 'none',
                    color: '#555', fontSize: 20, cursor: 'pointer',
                  }}>✕</button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px',
                  display: 'flex', flexDirection: 'column', gap: 14 }}>

                  {/* Info grid */}
                  <div style={{ display: 'grid',
                    gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {[
                      { label: 'Cliente',           value: modalDev.cliente },
                      { label: 'Cédula/RUC',        value: modalDev.cedula },
                      { label: 'Procesó NC',
                        value: modalDev.usuario_nc,
                        color: modalDev.mismo_vendedor ? '#e63946' : '#aaa' },
                      { label: 'Vendedor original',
                        value: modalDev.vendedor_original,
                        color: modalDev.mismo_vendedor ? '#e63946' : '#aaa' },
                      { label: 'Factura original',  value: modalDev.factura_orig },
                      { label: 'Fecha factura orig.',value: modalDev.fecha_factura },
                      { label: 'Total factura orig.',
                        value: `$${fmt(modalDev.total_factura)}` },
                      { label: 'Forma pago orig.',  value: modalDev.forma_pago_orig },
                    ].map(f => (
                      <div key={f.label} style={{ background: '#13131f',
                        borderRadius: 8, padding: '10px 12px' }}>
                        <div style={{ color: '#555', fontSize: 10,
                          textTransform: 'uppercase', letterSpacing: 0.5,
                          marginBottom: 4 }}>{f.label}</div>
                        <div style={{ color: f.color || '#fff',
                          fontSize: 13, fontWeight: f.color ? 700 : 400 }}>
                          {f.value || '—'}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Alertas */}
                  {(modalDev.mismo_vendedor || modalDev.sin_obs) && (
                    <div style={{ background: '#2a0d0d',
                      border: '1px solid #e6394655',
                      borderRadius: 9, padding: '12px 14px' }}>
                      <div style={{ color: '#e63946', fontWeight: 700,
                        fontSize: 13, marginBottom: 6 }}>
                        ⚠️ Alertas de control
                      </div>
                      {modalDev.mismo_vendedor && (
                        <div style={{ color: '#ffc107', fontSize: 12, marginBottom: 4 }}>
                          • El vendedor que procesó la NC es el mismo que realizó la venta original
                        </div>
                      )}
                      {modalDev.sin_obs && (
                        <div style={{ color: '#ffc107', fontSize: 12 }}>
                          • NC sin observación registrada
                        </div>
                      )}
                    </div>
                  )}

                  {/* Observación */}
                  {modalDev.observacion && (
                    <div style={{ background: '#13131f', borderRadius: 8,
                      padding: '10px 12px',
                      borderLeft: '3px solid #2a2a3e' }}>
                      <div style={{ color: '#555', fontSize: 10,
                        textTransform: 'uppercase', letterSpacing: 0.5,
                        marginBottom: 4 }}>Observación</div>
                      <div style={{ color: '#aaa', fontSize: 13 }}>
                        {modalDev.observacion}
                      </div>
                    </div>
                  )}

                  {/* ── Firma / Validación ─────────────────────────────── */}
                  {(() => {
                    const val = validaciones[modalDev.devid];
                    return val ? (
                      /* Ya validada */
                      <div style={{ background: '#0d2218',
                        border: '1px solid #4caf5055',
                        borderRadius: 9, padding: '14px 16px' }}>
                        <div style={{ color: '#4caf50', fontWeight: 700,
                          fontSize: 13, marginBottom: 8 }}>
                          ✅ NC Validada
                        </div>
                        <div style={{ display: 'grid',
                          gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <div>
                            <div style={{ color: '#555', fontSize: 10,
                              textTransform: 'uppercase', letterSpacing: 0.5,
                              marginBottom: 3 }}>Validado por</div>
                            <div style={{ color: '#4caf50', fontWeight: 700,
                              fontSize: 13 }}>{val.validado_por}</div>
                          </div>
                          <div>
                            <div style={{ color: '#555', fontSize: 10,
                              textTransform: 'uppercase', letterSpacing: 0.5,
                              marginBottom: 3 }}>Fecha</div>
                            <div style={{ color: '#aaa', fontSize: 13 }}>{val.fecha}</div>
                          </div>
                        </div>
                        {val.comentario && (
                          <div style={{ marginTop: 10,
                            background: '#13131f', borderRadius: 7,
                            padding: '8px 10px' }}>
                            <div style={{ color: '#555', fontSize: 10,
                              marginBottom: 3 }}>Comentario</div>
                            <div style={{ color: '#aaa', fontSize: 12 }}>
                              {val.comentario}
                            </div>
                          </div>
                        )}
                        {PUEDE_VALIDAR && (
                          <div style={{ marginTop: 10 }}>
                            <div style={{ color: '#555', fontSize: 11,
                              marginBottom: 6 }}>Actualizar comentario:</div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <input
                                value={comentario}
                                onChange={e => setComentario(e.target.value)}
                                placeholder="Nuevo comentario..."
                                style={{ flex: 1, background: '#0a0a14',
                                  border: '1px solid #2a2a3e', borderRadius: 7,
                                  color: '#fff', padding: '7px 10px',
                                  fontSize: 12, outline: 'none' }}
                              />
                              <button onClick={validarNC} disabled={validando}
                                style={{ background: '#1a3a2a',
                                  border: '1px solid #4caf5055',
                                  borderRadius: 7, color: '#4caf50',
                                  padding: '7px 14px', fontSize: 12,
                                  fontWeight: 700, cursor: 'pointer',
                                  opacity: validando ? 0.6 : 1 }}>
                                {validando ? '...' : '↺ Actualizar'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : PUEDE_VALIDAR ? (
                      /* Pendiente — mostrar formulario de firma */
                      <div style={{ background: '#1a1a0a',
                        border: '1px solid #ffc10733',
                        borderRadius: 9, padding: '14px 16px' }}>
                        <div style={{ color: '#ffc107', fontWeight: 700,
                          fontSize: 13, marginBottom: 10 }}>
                          ✍️ Firmar / Validar esta NC
                        </div>
                        <div style={{ color: '#555', fontSize: 12,
                          marginBottom: 10 }}>
                          Validando como:{' '}
                          <strong style={{ color: '#fff' }}>
                            {usuario?.nombre || usuarioKey}
                          </strong>
                        </div>
                        <textarea
                          value={comentario}
                          onChange={e => setComentario(e.target.value)}
                          placeholder="Comentario de validación (opcional): ej. Verificado con cámara, cliente confirmó, transacción correcta..."
                          rows={2}
                          style={{ width: '100%', background: '#0a0a14',
                            border: '1px solid #2a2a3e', borderRadius: 7,
                            color: '#fff', padding: '8px 10px',
                            fontSize: 12, outline: 'none', resize: 'vertical',
                            boxSizing: 'border-box', marginBottom: 10,
                            fontFamily: 'inherit' }}
                        />
                        <button onClick={validarNC} disabled={validando}
                          style={{ background: '#4361ee', border: 'none',
                            borderRadius: 8, color: '#fff',
                            padding: '10px 0', width: '100%',
                            fontSize: 13, fontWeight: 700,
                            cursor: validando ? 'not-allowed' : 'pointer',
                            opacity: validando ? 0.7 : 1 }}>
                          {validando ? '⏳ Registrando...' : '✅ Confirmar validación'}
                        </button>
                      </div>
                    ) : (
                      /* Sin permiso */
                      <div style={{ background: '#1a1a2e',
                        border: '1px solid #2a2a3e',
                        borderRadius: 9, padding: '12px 14px',
                        color: '#555', fontSize: 12, textAlign: 'center' }}>
                        🔒 Solo admin y Gabriela pueden validar NC
                      </div>
                    );
                  })()}

                  {/* Ítems devueltos */}
                  <div>
                    <div style={{ color: '#fff', fontWeight: 700,
                      fontSize: 14, marginBottom: 10 }}>
                      Ítems devueltos ({modalDev.items?.length || 0})
                    </div>
                    <div style={{ background: '#13131f', borderRadius: 9,
                      overflow: 'hidden' }}>
                      <div style={{ display: 'grid',
                        gridTemplateColumns: '100px 1fr 50px 80px 80px',
                        padding: '8px 12px', gap: 8,
                        color: '#555', fontSize: 10, fontWeight: 700,
                        textTransform: 'uppercase' }}>
                        <span>Código</span>
                        <span>Descripción</span>
                        <span style={{ textAlign: 'center' }}>Cant.</span>
                        <span style={{ textAlign: 'right' }}>Precio</span>
                        <span style={{ textAlign: 'right' }}>Total</span>
                      </div>
                      {(modalDev.items || []).map((it, i) => (
                        <div key={i} style={{
                          display: 'grid',
                          gridTemplateColumns: '100px 1fr 50px 80px 80px',
                          padding: '10px 12px', gap: 8, alignItems: 'center',
                          background: i % 2 === 0 ? '#1a1a2e' : '#13131f',
                          borderTop: '1px solid #0a0a14',
                        }}>
                          <span style={{ color: '#4cc9f0', fontFamily: 'monospace',
                            fontSize: 11 }}>{it.codigo}</span>
                          <span style={{ color: '#ccc', fontSize: 12 }}>
                            {it.descripcion}
                          </span>
                          <span style={{ textAlign: 'center', color: '#fff',
                            fontWeight: 700 }}>{it.cantidad.toFixed(0)}</span>
                          <span style={{ textAlign: 'right', color: '#aaa',
                            fontFamily: 'monospace', fontSize: 12 }}>
                            ${fmt(it.precio)}
                          </span>
                          <span style={{ textAlign: 'right', color: '#e63946',
                            fontFamily: 'monospace', fontWeight: 700 }}>
                            ${fmt(it.total)}
                          </span>
                        </div>
                      ))}
                      <div style={{ display: 'grid',
                        gridTemplateColumns: '100px 1fr 50px 80px 80px',
                        padding: '10px 12px', gap: 8,
                        background: '#0a0a14',
                        borderTop: '2px solid #2a2a3e' }}>
                        <span />
                        <span style={{ color: '#555', fontSize: 11,
                          fontWeight: 700 }}>TOTAL NC</span>
                        <span />
                        <span />
                        <span style={{ textAlign: 'right', color: '#e63946',
                          fontFamily: 'monospace', fontWeight: 700, fontSize: 15 }}>
                          ${fmt(modalDev.total)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const selStyle = {
  background: '#1e1e2e', border: '1px solid #2a2a3e',
  borderRadius: 8, color: '#fff', padding: '8px 12px',
  fontSize: 12, outline: 'none', cursor: 'pointer',
};
const btnStyle = {
  background: '#1e1e2e', border: '1px solid #2a2a3e',
  borderRadius: 8, color: '#555', padding: '8px 12px',
  fontSize: 13, cursor: 'pointer',
};